/**
 * rigidRegister.mjs — жёсткая (6 DOF) регистрация двух объёмов по взаимной
 * информации (Mutual Information). Без браузерных зависимостей — чистая
 * математика, чтобы совмещать КТ и МРТ (разные модальности → нужна именно MI,
 * а не разница яркостей).
 *
 * Объём: { data:Float32Array, dims:[nx,ny,nz], spacing:[sx,sy,sz], origin:[ox,oy,oz] }
 *   (оси предполагаются осеориентированными; направляющие косинусы можно
 *    добавить позже — ядро от этого не меняется).
 *
 * Преобразование задаётся 6 параметрами [tx,ty,tz, rx,ry,rz] (мм и радианы),
 * поворот — вокруг центра фиксированного объёма.
 *
 * Экспорт:
 *   rigidMatrix(params, center)     → функция world→world
 *   mutualInformation(fx, mv, p, o) → значение MI (больше = лучше совмещение)
 *   registerRigid(fixed, moving, o) → { params, mi, iterations }
 */

// ── геометрия ──────────────────────────────────────────────────────────────
// vol.direction (опц.) — 9 чисел Cornerstone: [ix,iy,iz, jx,jy,jz, kx,ky,kz],
// где столбцы — мировые направления осей i,j,k. Без него оси осеориентированы.
function _mat3inv(m) {
  const [a, b, c, d, e, f, g, h, i] = m;   // row-major
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-12) return null;
  const id = 1 / det;
  return [
    A * id, (c * h - b * i) * id, (b * f - c * e) * id,
    B * id, (a * i - c * g) * id, (c * d - a * f) * id,
    C * id, (b * g - a * h) * id, (a * e - b * d) * id,
  ];
}
export function idxToWorld(vol, i, j, k) {
  const s = vol.spacing, o = vol.origin, D = vol.direction;
  if (!D) return [o[0] + i * s[0], o[1] + j * s[1], o[2] + k * s[2]];
  const a = i * s[0], b = j * s[1], c = k * s[2];
  return [
    o[0] + D[0] * a + D[3] * b + D[6] * c,
    o[1] + D[1] * a + D[4] * b + D[7] * c,
    o[2] + D[2] * a + D[5] * b + D[8] * c,
  ];
}
export function worldToIdx(vol, w) {
  const s = vol.spacing, o = vol.origin, D = vol.direction;
  const d0 = w[0] - o[0], d1 = w[1] - o[1], d2 = w[2] - o[2];
  if (!D) return [d0 / s[0], d1 / s[1], d2 / s[2]];
  // M (row-major) со столбцами colI,colJ,colK; кэшируем обратную на объёме
  let inv = vol._dirInv;
  if (!inv) {
    inv = _mat3inv([D[0], D[3], D[6], D[1], D[4], D[7], D[2], D[5], D[8]]);
    vol._dirInv = inv || false;
  }
  if (!inv) return [d0 / s[0], d1 / s[1], d2 / s[2]];   // вырожденная — откат
  const p0 = inv[0] * d0 + inv[1] * d1 + inv[2] * d2;
  const p1 = inv[3] * d0 + inv[4] * d1 + inv[5] * d2;
  const p2 = inv[6] * d0 + inv[7] * d1 + inv[8] * d2;
  return [p0 / s[0], p1 / s[1], p2 / s[2]];
}

/** Матрица поворота R = Rz·Ry·Rx. */
function rotMat(rx, ry, rz) {
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const cy = Math.cos(ry), sy = Math.sin(ry);
  const cz = Math.cos(rz), sz = Math.sin(rz);
  // Rz*Ry*Rx
  return [
    cz * cy,             cz * sy * sx - sz * cx,  cz * sy * cx + sz * sx,
    sz * cy,             sz * sy * sx + cz * cx,  sz * sy * cx - cz * sx,
    -sy,                 cy * sx,                 cy * cx,
  ];
}

/**
 * Применить найденное преобразование к ГЕОМЕТРИИ тома (origin+direction), не
 * трогая воксели: том встаёт в кадр fixed и рисуется поверх (fusion).
 *
 * Регистрация даёт T: fixed(w) ≈ moving(T(w)). Значит содержимое moving в точке x
 * соответствует fixed в T⁻¹(x) — чтобы показать moving в кадре fixed, надо
 * переставить его геометрию преобразованием T⁻¹.
 *
 * @returns {{origin:number[], direction:number[]}} новые origin и direction (9)
 */
export function applyRigidToGeometry(vol, params, center) {
  const [tx, ty, tz, rx, ry, rz] = params;
  const R = rotMat(rx, ry, rz);
  const Ri = [R[0], R[3], R[6], R[1], R[4], R[7], R[2], R[5], R[8]]; // Rᵀ = R⁻¹
  const c = center;
  const mul = (v) => [
    Ri[0] * v[0] + Ri[1] * v[1] + Ri[2] * v[2],
    Ri[3] * v[0] + Ri[4] * v[1] + Ri[5] * v[2],
    Ri[6] * v[0] + Ri[7] * v[1] + Ri[8] * v[2],
  ];
  const o = vol.origin;
  // T⁻¹(o) = R⁻¹(o - c - t) + c
  const shifted = [o[0] - c[0] - tx, o[1] - c[1] - ty, o[2] - c[2] - tz];
  const ro = mul(shifted);
  const newOrigin = [ro[0] + c[0], ro[1] + c[1], ro[2] + c[2]];
  const D = vol.direction || [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const cI = mul([D[0], D[1], D[2]]);
  const cJ = mul([D[3], D[4], D[5]]);
  const cK = mul([D[6], D[7], D[8]]);
  return {
    origin: newOrigin,
    direction: [cI[0], cI[1], cI[2], cJ[0], cJ[1], cJ[2], cK[0], cK[1], cK[2]],
  };
}

/** 4×4 матрица (row-major) преобразования world→world вокруг центра —
 *  для сохранения/загрузки совмещения. M·[x,y,z,1]ᵀ = rigidMatrix(...)(x). */
export function paramsToMatrix4(params, center) {
  const [tx, ty, tz, rx, ry, rz] = params;
  const R = rotMat(rx, ry, rz);
  const c = center;
  // T(x) = R(x-c)+c+t = R·x + (c - R·c + t)
  const Rc = [
    R[0] * c[0] + R[1] * c[1] + R[2] * c[2],
    R[3] * c[0] + R[4] * c[1] + R[5] * c[2],
    R[6] * c[0] + R[7] * c[1] + R[8] * c[2],
  ];
  const b = [c[0] - Rc[0] + tx, c[1] - Rc[1] + ty, c[2] - Rc[2] + tz];
  return [
    R[0], R[1], R[2], b[0],
    R[3], R[4], R[5], b[1],
    R[6], R[7], R[8], b[2],
    0, 0, 0, 1,
  ];
}

/** Возвращает функцию world→world для параметров вокруг центра. */
export function rigidMatrix(params, center) {
  const [tx, ty, tz, rx, ry, rz] = params;
  const R = rotMat(rx, ry, rz);
  const cx = center[0], cy = center[1], cz = center[2];
  return (w) => {
    const x = w[0] - cx, y = w[1] - cy, z = w[2] - cz;
    return [
      R[0] * x + R[1] * y + R[2] * z + cx + tx,
      R[3] * x + R[4] * y + R[5] * z + cy + ty,
      R[6] * x + R[7] * y + R[8] * z + cz + tz,
    ];
  };
}

/** Трилинейная выборка (вне объёма → outVal). */
export function sampleTrilinear(vol, idx, outVal = 0) {
  const [nx, ny, nz] = vol.dims;
  const x = idx[0], y = idx[1], z = idx[2];
  if (x < 0 || y < 0 || z < 0 || x > nx - 1 || y > ny - 1 || z > nz - 1) return outVal;
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const x1 = Math.min(x0 + 1, nx - 1), y1 = Math.min(y0 + 1, ny - 1), z1 = Math.min(z0 + 1, nz - 1);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const d = vol.data;
  const id = (i, j, k) => i + nx * (j + ny * k);
  const c000 = d[id(x0, y0, z0)], c100 = d[id(x1, y0, z0)];
  const c010 = d[id(x0, y1, z0)], c110 = d[id(x1, y1, z0)];
  const c001 = d[id(x0, y0, z1)], c101 = d[id(x1, y0, z1)];
  const c011 = d[id(x0, y1, z1)], c111 = d[id(x1, y1, z1)];
  const c00 = c000 * (1 - fx) + c100 * fx;
  const c10 = c010 * (1 - fx) + c110 * fx;
  const c01 = c001 * (1 - fx) + c101 * fx;
  const c11 = c011 * (1 - fx) + c111 * fx;
  const c0 = c00 * (1 - fy) + c10 * fy;
  const c1 = c01 * (1 - fy) + c11 * fy;
  return c0 * (1 - fz) + c1 * fz;
}

function rangeOf(vol) {
  let mn = Infinity, mx = -Infinity;
  const d = vol.data;
  for (let i = 0; i < d.length; i++) { const v = d[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
  return [mn, mx];
}

/**
 * Взаимная информация между fixed и moving при преобразовании params.
 * Сэмплируем регулярную подсетку фиксированного объёма (шаг step), берём
 * moving в T(world). Совместная гистограмма bins×bins → MI в натах.
 */
export function mutualInformation(fixed, moving, params, o = {}) {
  const bins = o.bins || 32;
  const step = o.step || 2;
  const [fmin, fmax] = o.fixedRange || rangeOf(fixed);
  const [mmin, mmax] = o.movingRange || rangeOf(moving);
  const center = o.center || volumeCenterWorld(fixed);
  const T = rigidMatrix(params, center);

  const fScale = (bins - 1) / Math.max(1e-6, fmax - fmin);
  const mScale = (bins - 1) / Math.max(1e-6, mmax - mmin);

  const joint = new Float64Array(bins * bins);
  const [nx, ny, nz] = fixed.dims;
  let total = 0;      // выборок, попавших ВНУТРЬ moving
  let visited = 0;    // всего пройдено узлов подсетки

  for (let k = 0; k < nz; k += step) {
    for (let j = 0; j < ny; j += step) {
      for (let i = 0; i < nx; i += step) {
        visited += 1;
        const fv = fixed.data[i + nx * (j + ny * k)];
        const w = idxToWorld(fixed, i, j, k);
        const wm = T(w);
        const mi = worldToIdx(moving, wm);
        // вне moving — пропускаем (не штрафуем перекрытием фона)
        if (mi[0] < 0 || mi[1] < 0 || mi[2] < 0
          || mi[0] > moving.dims[0] - 1 || mi[1] > moving.dims[1] - 1 || mi[2] > moving.dims[2] - 1) continue;
        const mv = sampleTrilinear(moving, mi, mmin);
        let bf = ((fv - fmin) * fScale) | 0;
        let bm = ((mv - mmin) * mScale) | 0;
        if (bf < 0) bf = 0; else if (bf >= bins) bf = bins - 1;
        if (bm < 0) bm = 0; else if (bm >= bins) bm = bins - 1;
        joint[bf * bins + bm] += 1;
        total += 1;
      }
    }
  }
  // [7.0.28] Наружу — сколько выборок реально перекрылось: по этому числу
  // модуль отличает НАСТОЯЩЕЕ совмещение от «MI=0, объёмы не пересеклись».
  if (o.stats) { o.stats.used = total; o.stats.visited = visited; }
  if (total < 8) return 0;

  const pf = new Float64Array(bins), pm = new Float64Array(bins);
  for (let a = 0; a < bins; a++) {
    for (let b = 0; b < bins; b++) {
      const c = joint[a * bins + b];
      if (c) { pf[a] += c; pm[b] += c; }
    }
  }
  const inv = 1 / total;
  let mi = 0;
  for (let a = 0; a < bins; a++) {
    if (!pf[a]) continue;
    const pa = pf[a] * inv;
    for (let b = 0; b < bins; b++) {
      const c = joint[a * bins + b];
      if (!c) continue;
      const pab = c * inv;
      const pb = pm[b] * inv;
      mi += pab * Math.log(pab / (pa * pb));
    }
  }
  return mi;   // наты; больше = лучше
}

export function volumeCenterWorld(vol) {
  return idxToWorld(vol, (vol.dims[0] - 1) / 2, (vol.dims[1] - 1) / 2, (vol.dims[2] - 1) / 2);
}

/**
 * [7.0.29] Обрезать том до кубической области ±halfMm вокруг мировой точки
 * centerWorld. Возвращает ПОДтом того же spacing/direction — как «вырезать
 * голову из КТ всего тела»: у КТ всего тела центр в животе, свести его с МРТ
 * головы нечем; обрезав до головы, получаем совпадающие центры и работающий
 * захват MI. Осей не поворачиваем: берём индексный бокс, покрывающий мировой
 * куб (проекция 8 углов → мин/макс индекс, с запасом на наклон).
 *
 * @returns {{data,dims,spacing,origin,direction, cropped:true, keptFraction}}
 *          или исходный том, если бокс покрывает его целиком.
 */
export function cropVolumeToBox(vol, centerWorld, halfMm) {
  const [nx, ny, nz] = vol.dims;
  let i0 = Infinity, j0 = Infinity, k0 = Infinity, i1 = -Infinity, j1 = -Infinity, k1 = -Infinity;
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
    const w = [centerWorld[0] + sx * halfMm, centerWorld[1] + sy * halfMm, centerWorld[2] + sz * halfMm];
    const idx = worldToIdx(vol, w);
    i0 = Math.min(i0, idx[0]); i1 = Math.max(i1, idx[0]);
    j0 = Math.min(j0, idx[1]); j1 = Math.max(j1, idx[1]);
    k0 = Math.min(k0, idx[2]); k1 = Math.max(k1, idx[2]);
  }
  i0 = Math.max(0, Math.floor(i0)); j0 = Math.max(0, Math.floor(j0)); k0 = Math.max(0, Math.floor(k0));
  i1 = Math.min(nx - 1, Math.ceil(i1)); j1 = Math.min(ny - 1, Math.ceil(j1)); k1 = Math.min(nz - 1, Math.ceil(k1));
  if (i1 < i0 || j1 < j0 || k1 < k0) throw new Error('Область обрезки вне тома — сдвиньте перекрестье на голову.');
  // покрывает весь том → возвращаем как есть
  if (i0 === 0 && j0 === 0 && k0 === 0 && i1 === nx - 1 && j1 === ny - 1 && k1 === nz - 1) {
    return { ...vol, cropped: false, keptFraction: 1 };
  }
  const mx = i1 - i0 + 1, my = j1 - j0 + 1, mz = k1 - k0 + 1;
  const out = new Float32Array(mx * my * mz);
  const d = vol.data;
  let p = 0;
  for (let k = k0; k <= k1; k++) {
    for (let j = j0; j <= j1; j++) {
      const base = i0 + nx * (j + ny * k);
      for (let i = 0; i < mx; i++) out[p++] = d[base + i];
    }
  }
  return {
    data: out, dims: [mx, my, mz],
    spacing: vol.spacing, direction: vol.direction,
    origin: idxToWorld(vol, i0, j0, k0),
    cropped: true, keptFraction: (mx * my * mz) / (nx * ny * nz),
  };
}

// ── оптимизатор: Powell + золотое сечение ───────────────────────────────────
function goldenLineMin(f, x0, dir, opts) {
  const GR = 0.6180339887;
  // брекетинг вдоль dir
  let a = 0, b = opts.initStep;
  const fx = t => f(x0.map((v, i) => v + t * dir[i]));
  let fa = fx(a), fb = fx(b);
  if (fb > fa) { // идём в другую сторону
    b = -b; fb = fx(b);
    if (fb > fa) { // оба хуже — минимум около 0
      // маленький брекет вокруг 0
      let lo = -Math.abs(opts.initStep), hi = Math.abs(opts.initStep);
      return brentLike(fx, lo, 0, hi, opts.tol, opts.maxLine);
    }
  }
  // расширяем, пока падает
  let c = b + (b - a) * 1.618, fc = fx(c), guard = 0;
  while (fc < fb && guard++ < 40) {
    a = b; fa = fb; b = c; fb = fc;
    c = b + (b - a) * 1.618; fc = fx(c);
  }
  const lo = Math.min(a, c), hi = Math.max(a, c);
  return brentLike(fx, lo, b, hi, opts.tol, opts.maxLine);
}
function brentLike(fx, lo, mid, hi, tol, maxIt) {
  const GR = 0.6180339887;
  let a = lo, b = hi;
  let x = mid, fxv = fx(x);
  let it = 0;
  while ((b - a) > tol && it++ < maxIt) {
    let t;
    if ((x - a) > (b - x)) t = x - GR * (x - a);
    else t = x + GR * (b - x);
    const ft = fx(t);
    if (ft < fxv) { if (t < x) b = x; else a = x; x = t; fxv = ft; }
    else { if (t < x) a = t; else b = t; }
  }
  return { t: x, f: fxv };
}

/**
 * Powell: минимизируем f(params). Возвращает {x, f, iters}.
 * dirsScale — характерный шаг по каждому параметру.
 */
function powell(f, x0, dirsScale, opts = {}) {
  const n = x0.length;
  const tol = opts.tol || 1e-4;
  const maxIter = opts.maxIter || 30;
  const lineOpts = { initStep: 1, tol: opts.lineTol || 1e-3, maxLine: opts.maxLine || 40 };

  let x = x0.slice();
  let fx = f(x);
  // направления — масштабированные орты
  let dirs = [];
  for (let i = 0; i < n; i++) { const d = new Array(n).fill(0); d[i] = dirsScale[i]; dirs.push(d); }

  for (let iter = 0; iter < maxIter; iter++) {
    const xStart = x.slice(), fStart = fx;
    let biggestDrop = 0, biggestIdx = 0;

    for (let i = 0; i < n; i++) {
      const before = fx;
      const r = goldenLineMin(f, x, dirs[i], lineOpts);
      x = x.map((v, k) => v + r.t * dirs[i][k]);
      fx = r.f;
      const drop = before - fx;
      if (drop > biggestDrop) { biggestDrop = drop; biggestIdx = i; }
    }

    if (2 * (fStart - fx) <= tol * (Math.abs(fStart) + Math.abs(fx) + 1e-12)) break;

    // новое направление = суммарный сдвиг
    const newDir = x.map((v, k) => v - xStart[k]);
    const r = goldenLineMin(f, x, newDir, lineOpts);
    x = x.map((v, k) => v + r.t * newDir[k]);
    fx = r.f;
    // заменяем направление наибольшего падения
    dirs[biggestIdx] = newDir;
  }
  return { x, f: fx, iters: maxIter };
}

/**
 * Жёсткая регистрация moving→fixed по MI.
 * Возвращает { params:[tx,ty,tz,rx,ry,rz], mi, center }.
 */
export function registerRigid(fixed, moving, o = {}) {
  const center = volumeCenterWorld(fixed);
  const fixedRange = rangeOf(fixed), movingRange = rangeOf(moving);

  // [6.7.37] Шаг прореживания подбираем так, чтобы одно вычисление MI стоило
  // ограниченного числа выборок. Раньше вызывающий код передавал step = 1, и на
  // настоящей КТ (512×512×200, прорежённой втрое → 1.96 млн вокселей) полное
  // совмещение требовало ~1.4 млрд выборок ≈ 130 с при наглухо замороженной
  // вкладке. При 120 тыс. выборок на вызов та же задача укладывается в единицы
  // секунд, а точность не страдает: MI устойчиво оценивается и по подвыборке
  // (в ITK/Elastix берут вовсе 2–5 тыс. точек). Проверено на фантоме — reg_test.
  const nVox = fixed.dims[0] * fixed.dims[1] * fixed.dims[2];
  const budget = o.maxSamples || 200000;
  const autoStep = Math.max(1, Math.ceil(Math.cbrt(nVox / budget)));
  const fineStep = o.step || autoStep;
  const coarseStep = Math.max(fineStep, o.coarseStep || fineStep * 2);

  const base = { bins: o.bins || 32, fixedRange, movingRange, center };
  const negC = (p) => -mutualInformation(fixed, moving, p, { ...base, step: coarseStep });
  const negF = (p) => -mutualInformation(fixed, moving, p, { ...base, step: fineStep });

  // масштабы: перевод в мм (примерно размер вокселя), поворот ~2°
  const tScale = (o.tStep || 2) * Math.min(...fixed.spacing);
  const rScale = (o.rStep || 2) * Math.PI / 180;

  const initial = (o.initial || [0, 0, 0, 0, 0, 0]).slice();

  // Полная двухэтапная оптимизация из заданной затравки.
  //   Этап A: только смещение, на грубой подсетке (устойчивый захват, дёшево).
  //   Этап B: все 6 DOF на бюджетной подсетке с ПОЛНЫМИ шагами поиска.
  //   (Третий «уточняющий» этап с вдвое меньшими шагами делал хуже — на 8°
  //    повороте застревал на 0.25°: укороченный отрезок золотого сечения не
  //    дотягивался до истинного угла.)
  const optimizeFrom = (seed) => {
    const p = seed.slice();
    const a = powell(
      (t) => negC([t[0], t[1], t[2], p[3], p[4], p[5]]),
      [p[0], p[1], p[2]], [tScale, tScale, tScale], { maxIter: o.maxIter || 20 });
    p[0] = a.x[0]; p[1] = a.x[1]; p[2] = a.x[2];
    const b = powell(negF, p, [tScale, tScale, tScale, rScale, rScale, rScale], { maxIter: o.maxIter || 30 });
    return { x: b.x, f: b.f, iters: b.iters };
  };

  // [7.0.28] Затравка по центрам. Powell лезет в гору MI по локальному
  // градиенту — но градиент НУЛЕВОЙ, если объёмы в мировых координатах стоят
  // врозь (КТ и МРТ из разных исследований имеют разный Frame of Reference и
  // смещены на десятки см). Тогда захват невозможен и выходит «MI=0». Добавляем
  // второй старт со сведёнными центрами. Оптимизируем из ОБЕИХ затравок и берём
  // лучшую по MI — так результат гарантированно не хуже прежнего (старт из
  // [0,0,0] всегда среди кандидатов; чистый поворот вокруг центра он и выигрывает,
  // а смещённую пару спасает центроидная затравка). Если оба финала ~0 (КТ всего
  // тела vs МРТ головы — центры в разных частях тела), проверка ok честно
  // объявит неудачу.
  const seeds = [initial];
  if (o.seedCentroid !== false) {
    const cf = volumeCenterWorld(fixed), cm = volumeCenterWorld(moving);
    const dt = [cm[0] - cf[0], cm[1] - cf[1], cm[2] - cf[2]];
    // добавляем центроидную затравку, только если она заметно отличается от старта
    if (Math.hypot(dt[0] - initial[0], dt[1] - initial[1], dt[2] - initial[2]) > Math.min(...fixed.spacing)) {
      seeds.push([dt[0], dt[1], dt[2], initial[3], initial[4], initial[5]]);
    }
  }

  let full = null;
  for (const seed of seeds) {
    const r = optimizeFrom(seed);
    if (!full || r.f < full.f) full = r;   // f = −MI, меньше = лучше
  }

  const samples = Math.ceil(fixed.dims[0] / fineStep) * Math.ceil(fixed.dims[1] / fineStep)
    * Math.ceil(fixed.dims[2] / fineStep);

  // [7.0.28] Честная оценка качества на итоговых параметрах: MI и ДОЛЯ
  // перекрытия (сколько выборок fixed легло внутрь moving). ok=false →
  // совмещения по сути нет, и вызывающий НЕ должен показывать «совмещено» и
  // двигать том. Пороги мягкие: настоящая КТ-МРТ голова даёт MI ≳ 0.2 и
  // перекрытие в разы больше; «объёмы врозь» — MI≈0 и десятки выборок.
  const stats = {};
  const finalMi = mutualInformation(fixed, moving, full.x, { ...base, step: fineStep, stats });
  const overlap = stats.visited ? stats.used / stats.visited : 0;
  const minMI = o.minMI ?? 0.05;
  const minUsed = o.minUsed ?? 200;
  const ok = finalMi >= minMI && stats.used >= minUsed;

  return {
    params: full.x, mi: finalMi, center, iters: full.iters, step: fineStep,
    samplesPerEval: samples, overlap, samplesUsed: stats.used, ok,
  };
}
